import { translate } from "@/i18n";
import { onboardingGuideMessages } from "@/i18n/messages/onboarding-guide";
import {
  shortcutPlatform,
  type ShortcutId,
  type ShortcutPlatform,
} from "@/lib/shortcut-registry";

export type GuideSlideId = "type" | "voice" | "workspace";

/** What the guide says: a slide's action, its close step once the real
 *  surface is open, or the wrap-up once the last slide's lesson is done. */
export type GuideLesson =
  | GuideSlideId
  | `${GuideSlideId}:close`
  | "voice:draw"
  | "done"
  | "ready";

type GuideSlide = {
  id: GuideSlideId;
  shortcut: ShortcutId;
  /** A surface that covers the display: the card steps aside this long
   *  once it really opens, then returns with the close step and its line. */
  yieldMs?: number;
};

export const GUIDE_SLIDES: [GuideSlide, GuideSlide, GuideSlide] = [
  { id: "type", shortcut: "shortcut.input.open" },
  { id: "voice", shortcut: "shortcut.voice.toggle" },
  { id: "workspace", shortcut: "shortcut.toggle_workspace", yieldMs: 2_000 },
];

type GuideKey = keyof typeof onboardingGuideMessages.en;

const SLIDE_TEXT = {
  type: {
    title: "typeTitle",
    description: "typeDescription",
    open: "typeOpenLine",
    close: "typeCloseLine",
  },
  voice: {
    title: "voiceTitle",
    description: "voiceDescription",
    open: "voiceOpenLine",
    close: "voiceCloseLine",
  },
  workspace: {
    title: "workspaceTitle",
    description: "workspaceDescription",
    open: "workspaceOpenLine",
    close: "workspaceCloseLine",
  },
} as const satisfies Record<
  GuideSlideId,
  Record<"title" | "description" | "open" | "close", GuideKey>
>;

export function guideSlideTitle(id: GuideSlideId): string {
  return translate(onboardingGuideMessages, SLIDE_TEXT[id].title);
}

export function guideSlideDescription(id: GuideSlideId): string {
  return translate(onboardingGuideMessages, SLIDE_TEXT[id].description);
}

/** The line spoken when the slide shows: "<shortcut> to open …". */
export function guideOpenLine(id: GuideSlideId, shortcut: string): string {
  return translate(onboardingGuideMessages, SLIDE_TEXT[id].open, {
    shortcut,
  });
}

/** The close step once the real surface is open: "<shortcut> again to …". */
export function guideCloseLine(id: GuideSlideId, shortcut: string): string {
  return translate(onboardingGuideMessages, SLIDE_TEXT[id].close, {
    shortcut,
  });
}

/** Confirms a real open, on the card and at the start of the close line. */
export function guideDoneText(
  id: GuideSlideId,
  platform: ShortcutPlatform = shortcutPlatform(),
): string {
  if (id === "type") return translate(onboardingGuideMessages, "typeDone");
  if (id === "workspace")
    return translate(onboardingGuideMessages, "workspaceDone");
  // Listening shows only on Denker's menu bar or taskbar icon.
  return translate(
    onboardingGuideMessages,
    platform === "windows" ? "voiceDoneWindows" : "voiceDoneMac",
  );
}

/** The open surface that covers the display, if any. When it really opens
 *  the card steps aside for its `yieldMs`, then shows and reads its close
 *  step. */
export function guideCoveringSlide(
  open: readonly GuideSlideId[] = [],
): GuideSlide | undefined {
  return GUIDE_SLIDES.find((slide) => slide.yieldMs && open.includes(slide.id));
}
